

import { supabase } from '@/integrations/supabase/client';

/**
 * Helper function for local storage fallback
 * Saves data to localStorage as a fallback when Supabase operations fail
 */
export const saveToLocalStorageFallback = <T>(key: string, data: T): void => {
  try {
    const items = JSON.parse(localStorage.getItem(key) || '[]');
    // Add a timestamp for synchronization purposes
    const itemWithTimestamp = { ...data, timestamp: new Date().toISOString(), synced: false };
    items.push(itemWithTimestamp);
    localStorage.setItem(key, JSON.stringify(items));
    console.log(`Saved to localStorage fallback for key: ${key}`);
  } catch (err) {
    console.error(`Error saving to localStorage fallback for key: ${key}:`, err);
  }
};

/**
 * Helper function to retrieve data from localStorage
 * Used as a fallback when Supabase operations fail
 */
export const getFromLocalStorage = <T>(key: string): T[] => {
  try {
    return JSON.parse(localStorage.getItem(key) || '[]');
  } catch (err) {
    console.error(`Error getting data from localStorage for key: ${key}:`, err);
    return [];
  }
};

/**
 * Function to check if device is online
 */
export const isOnline = (): boolean => {
  return navigator.onLine;
};

/**
 * Function to sync data to Supabase when coming back online
 * @param dataKey The localStorage key for the data to sync
 * @param syncFn The function to call to sync the data
 */
export const syncToSupabaseWhenOnline = <T>(dataKey: string, syncFn: (item: T) => Promise<any>): void => {
  // Only attempt to sync if online
  if (!isOnline()) return;
  
  try {
    // Get all items from local storage
    const items = getFromLocalStorage<T & { synced?: boolean }>(dataKey);
    
    // Filter unsynced items
    const unsyncedItems = items.filter(item => !item.synced);
    
    if (unsyncedItems.length === 0) return;
    
    console.log(`Syncing ${unsyncedItems.length} unsynced ${dataKey} items to Supabase`);
    
    // Attempt to sync each unsynced item
    unsyncedItems.forEach(async (item, index) => {
      try {
        await syncFn(item);
        
        // Mark as synced
        items[items.indexOf(unsyncedItems[index])].synced = true;
        localStorage.setItem(dataKey, JSON.stringify(items));
        
        console.log(`Successfully synced item to Supabase`);
      } catch (err) {
        console.error(`Error syncing item to Supabase:`, err);
      }
    });
  } catch (err) {
    console.error(`Error in syncToSupabaseWhenOnline:`, err);
  }
};

// Register background sync if available
export const registerBackgroundSync = (tag: string = 'sync-data'): void => {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    navigator.serviceWorker.ready.then(registration => {
      try {
        // Fix: Use type assertion to access the sync property
        // The 'sync' property is available in modern browsers but TypeScript definitions might be missing it
        const reg = registration as any;
        if (reg.sync) {
          reg.sync.register(tag).then(() => {
            console.log('Background sync registered:', tag);
          }).catch((err: Error) => {
            console.error('Error registering background sync:', err);
          });
        } else {
          console.log('Background sync not supported in this browser');
        }
      } catch (err) {
        console.error('Error setting up background sync:', err);
      }
    });
  }
};

// Set up online listener to sync data when connection is restored
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    console.log('Device is back online, syncing data...');
    
    // Try to register background sync
    registerBackgroundSync();
    
    // Add sync functions for each data type here
    // Example sync functions would be called here for each data type
    // syncToSupabaseWhenOnline('selections', syncSelectionToSupabase);
    // syncToSupabaseWhenOnline('transfers', syncTransferToSupabase);
    // etc.
  });
}

// Function to check if we're in standalone/PWA mode
export const isPwa = (): boolean => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         (window.navigator as any).standalone === true;
};

